import { MergeQueue } from 'src/components/pipelines/models/MergeQueue';

import {
  WaitTimeUnit,
  MergeQueueBranchMode,
  MergeQueueEditFormState,
  MergeQueueEditPayload,
  MergeQueueFormState,
  MergeQueuePayload,
  MergeQueueStrategy,
  PullRequestsOrderingCriteria,
} from './types';

export const MERGE_QUEUES_PR_PAGE_LENGTH = 10;
export const MERGE_QUEUES_PAGE_LENGTH = 100;

export const convertWaitTime = (
  seconds: number
): { value: number; unit: WaitTimeUnit } => {
  if (seconds < 60) {
    return { value: seconds, unit: WaitTimeUnit.SECONDS };
  }
  if (seconds < 3600) {
    return {
      value: Math.floor(seconds / 60),
      unit: WaitTimeUnit.MINUTES,
    };
  }
  return {
    value: Math.floor(seconds / 3600),
    unit: WaitTimeUnit.HOURS,
  };
};

export const convertValueUnitToSeconds = (
  value: number,
  unit: WaitTimeUnit
): number => {
  switch (unit) {
    case WaitTimeUnit.SECONDS:
      return value;
    case WaitTimeUnit.MINUTES:
      return value * 60;
    case WaitTimeUnit.HOURS:
      return value * 3600;
    default:
      throw new Error(`Invalid WaitTimeUnit: ${unit}`);
  }
};

export const mergeQueueToFormValues = (
  mergeQueue: MergeQueue
): Partial<MergeQueueFormState> => {
  const {
    name,
    branch,
    buildConcurrency,
    maximumBatchWaitTimeInSeconds,
    mergeBatchSize,
    strategy,
    mergeStrategy,
    pullRequestsOrdering,
  } = mergeQueue;

  const { value: maximumBatchWaitTime, unit: maximumBatchWaitTimeUnit } =
    maximumBatchWaitTimeInSeconds
      ? convertWaitTime(maximumBatchWaitTimeInSeconds)
      : { value: 0, unit: WaitTimeUnit.MINUTES };

  return {
    name,
    targetBranchName: branch.name,
    strategy,
    mergeStrategy,
    buildConcurrency,
    mergeBatchSize,
    maximumBatchWaitTime,
    maximumBatchWaitTimeUnit,
    ...(pullRequestsOrdering
      ? {
          pullRequestsOrdering: {
            criteria: pullRequestsOrdering.criteria,
            sourceBranchPatterns:
              pullRequestsOrdering.criteria ===
              PullRequestsOrderingCriteria.BRANCH_PATTERN_AND_TIME_BASED
                ? pullRequestsOrdering.sourceBranchPatterns
                : [],
          },
        }
      : {}),
  };
};

export const formValuesToMergeQueue = (
  values: MergeQueueFormState & {
    pullRequestsOrderingCriteria?: PullRequestsOrderingCriteria;
    sourceBranchPatterns?: string;
  },
  options?: {
    isMergeQueuesLandingAreaEnabled?: boolean;
    isMergeQueuesStrategyEnabled?: boolean;
  }
): MergeQueuePayload => {
  const {
    name,
    targetBranchName,
    strategy,
    mergeStrategy,
    buildConcurrency,
    mergeBatchSize,
    maximumBatchWaitTime,
    maximumBatchWaitTimeUnit,
    pullRequestsOrderingCriteria,
    sourceBranchPatterns,
  } = values;

  const resolvedStrategy = options?.isMergeQueuesStrategyEnabled
    ? strategy ?? MergeQueueStrategy.STRICT_PER_PR
    : MergeQueueStrategy.STRICT_PER_PR;

  // Build pullrequests_ordering payload
  const pullrequestsOrdering = pullRequestsOrderingCriteria
    ? {
        pullrequests_ordering: {
          criteria: pullRequestsOrderingCriteria,
          source_branch_patterns:
            pullRequestsOrderingCriteria ===
              PullRequestsOrderingCriteria.BRANCH_PATTERN_AND_TIME_BASED &&
            sourceBranchPatterns
              ? sourceBranchPatterns
                  .split(',')
                  .map(p => p.trim())
                  .filter(Boolean)
              : [],
        },
      }
    : {};

  return {
    name,
    branch: {
      mode: MergeQueueBranchMode.BRANCH_NAME,
      name: targetBranchName,
    },
    strategy: resolvedStrategy,
    build_concurrency: Number(buildConcurrency),
    ...(mergeStrategy ? { merge_strategy: mergeStrategy } : {}),
    ...(options?.isMergeQueuesLandingAreaEnabled
      ? {
          maximum_batch_wait_time_in_seconds: convertValueUnitToSeconds(
            maximumBatchWaitTime || 0,
            maximumBatchWaitTimeUnit || WaitTimeUnit.MINUTES
          ),
          merge_batch_size: Number(mergeBatchSize || 1),
        }
      : {}),
    ...pullrequestsOrdering,
  };
};

export const formValuesToEditMergeQueue = (
  values: MergeQueueEditFormState & {
    strategy?: MergeQueueStrategy;
    pullRequestsOrderingCriteria?: PullRequestsOrderingCriteria;
    sourceBranchPatterns?: string;
  }
): MergeQueueEditPayload => {
  const {
    state,
    buildConcurrency,
    mergeStrategy,
    mergeBatchSize,
    maximumBatchWaitTime,
    maximumBatchWaitTimeUnit,
    pullRequestsOrderingCriteria,
    sourceBranchPatterns,
  } = values;

  const payload: MergeQueueEditPayload = {};

  if (state !== undefined) {
    payload.state = state;
  }

  if (buildConcurrency) {
    payload.build_concurrency = Number(buildConcurrency);
  }

  if (mergeStrategy) {
    payload.merge_strategy = mergeStrategy;
  }

  if (maximumBatchWaitTime) {
    payload.maximum_batch_wait_time_in_seconds = convertValueUnitToSeconds(
      maximumBatchWaitTime,
      maximumBatchWaitTimeUnit || WaitTimeUnit.MINUTES
    );
  }

  if (mergeBatchSize !== undefined) {
    payload.merge_batch_size = Number(mergeBatchSize ?? 1);
  }

  if (pullRequestsOrderingCriteria) {
    payload.pullrequests_ordering = {
      criteria: pullRequestsOrderingCriteria,
      source_branch_patterns:
        pullRequestsOrderingCriteria ===
          PullRequestsOrderingCriteria.BRANCH_PATTERN_AND_TIME_BASED &&
        sourceBranchPatterns
          ? sourceBranchPatterns
              .split(',')
              .map(p => p.trim())
              .filter(Boolean)
          : [],
    };
  }

  return payload;
};

export const preventNonNumericKeyPress = (event: KeyboardEvent) => {
  if (!/[0-9]/.test(event.key)) {
    event.preventDefault();
  }
};
